/**
 * Pílula e progresso do cartão (RDV-14, RDV-18), lidos das etapas da agenda: os textos de status são os que a linha
 * do tempo do `TaskItem` calcula. Nenhuma regra nova, só a tradução do status para o texto do design.
 */
import { EtapaDaAgenda } from './components/AgendaDaRealizacao';

const BONIFICADA = ['Bonificada', 'Bonificada - Finalizada', 'Bonificada - Prazo expirado'];
const SELO_DO_NIVEL_3 = /^Bonificada (Valeu|Mandou Bem|UAU) - Finalizada$/;
const PROMOVIDA_FINALIZADA = /^Promovida ao nível (\d) - Finalizada$/;

function nivelDa(etapa: EtapaDaAgenda): number | undefined {
  const nivel = /^Nível (\d)$/.exec(etapa.titulo);
  return nivel ? Number(nivel[1]) : undefined;
}

/** Texto da pílula de uma etapa pela tabela de RDV-14; vazio sem status ou em etapa sem texto próprio. */
function pilulaDaEtapa(etapa: EtapaDaAgenda): string {
  const { status } = etapa;
  if (status === 'Prazo expirado') return status;

  if (etapa.titulo === 'Validação') {
    if (status === 'Devolvida') return 'Não validada';
    return status === 'Aguardando validação' || status === 'Validada' ? status : '';
  }

  const nivel = nivelDa(etapa);
  if (!nivel) return '';

  if (status === 'Validada - Finalizada') return 'Validada';
  if (status === 'Aguardando apreciação') return `Aguardando apreciação · Nível ${nivel}`;
  if (BONIFICADA.includes(status) || status === 'Premiada - Finalizada') return `Bonificada no Nível ${nivel}`;
  if (status === 'Promovida') return `Promovida ao Nível ${nivel + 1}`;

  const selo = SELO_DO_NIVEL_3.exec(status);
  if (selo) return `Bonificada no Nível ${nivel} · ${selo[1]}`;

  // A linha do tempo põe "Promovida ao nível Y - Finalizada" na própria coluna do Nível Y: vale o nível do status.
  const promovida = PROMOVIDA_FINALIZADA.exec(status);
  if (promovida) return `Promovida ao Nível ${promovida[1]}`;

  return '';
}

const AGUARDANDO_NIVEL = /^Aguardando apreciação · Nível (\d)$/;

/** Nível de uma pílula "Aguardando apreciação · Nível X" (RDV-15); outra pílula, nenhum. */
export function nivelAguardado(pilula: string): number | undefined {
  const nivel = AGUARDANDO_NIVEL.exec(pilula);
  return nivel ? Number(nivel[1]) : undefined;
}

/** RDV-14: pílula do cartão, pelo status da etapa em que a realização está. */
export function pilulaDaRealizacao(etapas: EtapaDaAgenda[]): string {
  const atual = etapas.find(etapa => etapa.atual);
  return atual ? pilulaDaEtapa(atual) : '';
}

/**
 * O nível agiu: bonificou, premiou ou promoveu. "Promovida ao nível Y - Finalizada" fica de fora: a `timeline` a põe na
 * coluna do Nível Y, mas ela só repete a promoção do nível anterior, que já contou.
 */
const NIVEL_AGIU = /^(Bonificada|Premiada|Promovida$)/;
const ETAPA_FINALIZADA = / - Finalizada$/;

/**
 * RDV-18: "Em validação" na Validação; depois, os níveis que agiram, com "Concluída · " quando a realização terminou
 * (pílula "Bonificada no Nível X" ou status "… - Finalizada" na etapa atual). A devolvida não mostra progresso: a
 * pílula "Não validada" já diz onde ela está.
 */
export function progressoDaTrilha(etapas: EtapaDaAgenda[]): string {
  const atual = etapas.find(etapa => etapa.atual);
  if (atual?.titulo === 'Validação') return atual.status === 'Devolvida' ? '' : 'Em validação';

  const feitos = etapas.filter(etapa => nivelDa(etapa) && NIVEL_AGIU.test(etapa.status)).length;
  const concluida =
    pilulaDaRealizacao(etapas).startsWith('Bonificada no Nível') || ETAPA_FINALIZADA.test(atual?.status ?? '');

  return `${concluida ? 'Concluída · ' : ''}${feitos} de 3 níveis`;
}
